import React, { useContext }from "react";

import { useTranslation } from "react-i18next";

import { EventBodyTabs } from "@data-types/offer/event/EventViewStoreEnums";
import { LivePlayerStatistics, MatchStatistics } from "@features/offer/desktop";
import { EventInsightsSection } from "@features/offer/desktop/pages/event/components/event-insights/EventInsightsSection";
import { TeamStandingsStatistics } from "@features/offer/desktop/statistics/components/team-standings-stats";
import { OfferStoreContext } from "@gp/components";
import EventViewStore from "@state/stores/offer/event/EventViewStore";
import { AdditionalOfferViewStore } from "@state/stores/offer/shared/AdditionalOfferViewStore";
import classnames from "classnames";
import { observer } from "mobx-react";

import { EmptyContent, StreaksStatistics } from ".";
import { FullStatistics } from "../pages/event/components";
import EventStatisticsHeaderPills from "../pages/event/components/EventStatisticsHeaderPills";
import { TeamStandingsMultiSelectDropdown } from "../statistics/components/team-standings-stats/TeamStandingsMultiSelectDropdown";
import { TeamLineupsSection } from "../statistics/components/team-lineups/TeamLineupsSection";

export const EventStatisticsContent = observer(
	function EventStatisticsContent() {
		const store = useContext(OfferStoreContext) as
			| EventViewStore
			| AdditionalOfferViewStore;

		if (store.currentTab === EventBodyTabs.Statistics) {
			return <EventStatistics store={store} />;
		}
		return null;
	}
);

const EventStatistics = observer(function EventStatistics({
	store,
}: {
	store: EventViewStore | AdditionalOfferViewStore;
}) {
	const { t } = useTranslation();

	return (
		<>
			<div className="event__tabs event__tabs--flex">
				{store.sportStatisticsStore.isStreakStatsActive && (
					<>
						<div className="event__tabs__filter">
							<select
								id="streakTabFilter"
								className="event__tabs__select input input--sml input--select"
								value={store.sportStatisticsStore.streakFilter}
								onChange={(e) => {
									store.sportStatisticsStore.setStreakFilter(
										e.target.value
									);
								}}
							>
								{store.sportStatisticsStore.availableStreakFilterOptions.map(
									(option) => (
										<option
											key={option.key}
											value={option.key}
											className={classnames(
												"input input--select",
												{
													"btn--secondary is-active":
														store
															.sportStatisticsStore
															.streakFilter ===
														option.key,
													"btn--tertiary":
														store
															.sportStatisticsStore
															.streakFilter !==
														option.key,
												}
											)}
										>
											{t(option.title)}
										</option>
									)
								)}
							</select>
						</div>

						<div className="event__tabs__divider" />
					</>
				)}

				{store.sportStatisticsStore.isMatchStatActive && (
					<>
						<div className="event__tabs__filter">
							<select
								id="matchTabFilter"
								className="event__tabs__select input input--sml input--select"
								value={store.sportStatisticsStore.matchFilter}
								onChange={(e) => {
									store.sportStatisticsStore.setMatchFilter(
										e.target.value
									);
								}}
							>
								{store.sportStatisticsStore.availableMatchStatsFilterOptions.map(
									(option) => (
										<option
											key={option.key}
											value={option.key}
											className={classnames(
												"input input--select",
												{
													"btn--secondary is-active":
														store
															.sportStatisticsStore
															.matchFilter ===
														option.key,
													"btn--tertiary":
														store
															.sportStatisticsStore
															.streakFilter !==
														option.key,
												}
											)}
										>
											{t(option.title)}
										</option>
									)
								)}
							</select>
						</div>

						<div className="event__tabs__divider" />
					</>
				)}

				{store.playerStatisticsStore.isPlayerStatActive && (
					<>
						<div className="event__tabs__filter">
							<select
								id="playerTabFilter"
								className="event__tabs__select input input--sml input--select"
								onChange={(e) => {
									store.playerStatisticsStore.setPlayerFilter(
										e.target.value
									);
								}}
							>
								{store.playerStatisticsStore.playerStatsGroups.map(
									(group) =>
										group.data &&
										group.data.length > 0 && (
											<option
												key={group.key}
												value={group.key}
												className={classnames(
													"input input--select",
													{
														"btn--secondary is-active":
															store
																.playerStatisticsStore
																.playerFilter ===
															group.key,
														"btn--tertiary":
															store
																.playerStatisticsStore
																.playerFilter !==
															group.key,
													}
												)}
											>
												{t(group.title)}
											</option>
										)
								)}
							</select>
						</div>

						<div className="event__tabs__divider" />
					</>
				)}
				{store.teamStandingsStatsStore.isTeamStandingsStatsActive && (
                    <>
                        <TeamStandingsMultiSelectDropdown
                            store={store}
                        />
                        <div className="event__tabs__divider" />
                    </>
				)}
				<nav className="event__tabs__nav">
					<EventStatisticsHeaderPills store={store} />
				</nav>
			</div>

			{store.displayStatisticsGroup ? (
				<div
					className={classnames("event__offer", {
						"u-padd--top--reset": store.shouldShowStatisticsHeader,
					})}
				>
					<StreaksStatistics />
					<MatchStatistics />
					<LivePlayerStatistics />
					<EventInsightsSection />
					<TeamLineupsSection />
					<FullStatistics />
					<TeamStandingsStatistics />
				</div>
			) : (
				<EmptyContent
					message={t("EVENT.EMPTY_STATE.NO_STATS")}
					emptyIcon="u-icon--empty--search"
				/>
			)}
		</>
	);
});
